import 'cally';

export default function initBooking() {
    const forms = document.querySelectorAll('.js-booking-form');
    if (!forms.length) return;

    forms.forEach(form => {
        // --- Datepicker Logic ---
        const arrivalField = form.querySelector('.booking-form__field--date:nth-child(1)');
        const departureField = form.querySelector('.booking-form__field--date:nth-child(2)');

        if (arrivalField && departureField) {
            const arrivalInput = arrivalField.querySelector('input');
            const departureInput = departureField.querySelector('input');

            const today = new Date().toISOString().split('T')[0];

            const formatDate = (isoString) => {
                if (!isoString) return '';
                const date = new Date(isoString + 'T12:00:00');
                return date.toLocaleDateString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                });
            };

            const createPicker = (field, min) => {
                const wrapper = field.querySelector('.booking-form__input-wrapper');

                // Create the popup container
                const popup = document.createElement('div');
                popup.className = 'calendar-popup';
                popup.hidden = true;
                popup.setAttribute('role', 'dialog');
                popup.setAttribute('aria-modal', 'false');
                popup.setAttribute('aria-label', 'Choose date');

                // Create calendar-date — cally's top-level component
                const calendarDate = document.createElement('calendar-date');
                calendarDate.setAttribute('value', today);
                if (min) calendarDate.setAttribute('min', min);
                calendarDate.setAttribute('first-day-of-week', '1');
                calendarDate.setAttribute('show-outside-days', '');
                calendarDate.setAttribute('locale', 'en-GB');

                // Custom nav arrows via slots
                const arrowSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M4.99837 0.71811C4.62463 0.32692 3.99996 0.32692 3.62622 0.71811C3.27584 1.08485 3.27584 1.6623 3.62622 2.02904L8.37541 7L3.62622 11.971C3.27584 12.3377 3.27584 12.9152 3.62622 13.2819C3.99996 13.6731 4.62463 13.6731 4.99837 13.2819L11 7L4.99837 0.71811Z" fill="currentColor"/></svg>`;

                const prevSlot = document.createElement('span');
                prevSlot.slot = 'previous';
                prevSlot.className = 'calendar-nav-arrow calendar-nav-arrow--prev';
                prevSlot.innerHTML = arrowSvg;

                const nextSlot = document.createElement('span');
                nextSlot.slot = 'next';
                nextSlot.className = 'calendar-nav-arrow calendar-nav-arrow--next';
                nextSlot.innerHTML = arrowSvg;

                calendarDate.appendChild(prevSlot);
                calendarDate.appendChild(nextSlot);

                // Create calendar-month — the grid child
                const calendarMonth = document.createElement('calendar-month');
                calendarDate.appendChild(calendarMonth);

                popup.appendChild(calendarDate);
                wrapper.appendChild(popup);

                return { popup, calendarDate };
            };

            const arrival = createPicker(arrivalField, today);
            const departure = createPicker(departureField, today);

            // Set initial display values
            arrivalInput.value = formatDate(today);
            departureInput.value = formatDate(today);

            // Mark inputs as combobox popup triggers for screen readers
            arrival.popup.id = arrivalInput.id + '-popup';
            arrivalInput.setAttribute('role', 'combobox');
            arrivalInput.setAttribute('aria-haspopup', 'dialog');
            arrivalInput.setAttribute('aria-controls', arrival.popup.id);
            arrivalInput.setAttribute('aria-expanded', 'false');
            departure.popup.id = departureInput.id + '-popup';
            departureInput.setAttribute('role', 'combobox');
            departureInput.setAttribute('aria-haspopup', 'dialog');
            departureInput.setAttribute('aria-controls', departure.popup.id);
            departureInput.setAttribute('aria-expanded', 'false');

            // Arrival change handler
            arrival.calendarDate.addEventListener('change', (e) => {
                const val = e.target.value;
                arrivalInput.value = formatDate(val);

                // Update departure min so it can't be before arrival
                departure.calendarDate.setAttribute('min', val);
                if (departure.calendarDate.value && departure.calendarDate.value < val) {
                    departure.calendarDate.value = val;
                    departureInput.value = formatDate(val);
                }

                closePicker(arrival);
            });

            // Departure change handler
            departure.calendarDate.addEventListener('change', (e) => {
                departureInput.value = formatDate(e.target.value);
                closePicker(departure);
            });

            // Popup open/close helpers
            function getPickerInput(picker) {
                return picker === arrival ? arrivalInput : departureInput;
            }

            function openPicker(picker) {
                // Close any open popups first
                closeAllPickers();
                picker.popup.hidden = false;
                getPickerInput(picker).setAttribute('aria-expanded', 'true');
                picker.calendarDate.focus();
            }

            function closePicker(picker) {
                picker.popup.hidden = true;
                getPickerInput(picker).setAttribute('aria-expanded', 'false');
            }

            function closeAllPickers() {
                closePicker(arrival);
                closePicker(departure);
            }

            // Keyboard support: open calendar on Enter or Space
            function handleDateKeydown(e, picker) {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    const isOpen = !picker.popup.hidden;
                    if (isOpen) {
                        closePicker(picker);
                    } else {
                        openPicker(picker);
                    }
                }
            }

            arrivalInput.addEventListener('keydown', (e) => handleDateKeydown(e, arrival));
            departureInput.addEventListener('keydown', (e) => handleDateKeydown(e, departure));

            // Toggle on field click
            arrivalField.addEventListener('click', (e) => {
                if (e.target.closest('.calendar-popup')) return;
                e.stopPropagation();
                const isOpen = !arrival.popup.hidden;
                if (isOpen) {
                    closePicker(arrival);
                } else {
                    openPicker(arrival);
                }
            });

            departureField.addEventListener('click', (e) => {
                if (e.target.closest('.calendar-popup')) return;
                e.stopPropagation();
                const isOpen = !departure.popup.hidden;
                if (isOpen) {
                    closePicker(departure);
                } else {
                    openPicker(departure);
                }
            });

            // Close on outside click
            document.addEventListener('click', (e) => {
                if (!arrivalField.contains(e.target)) {
                    closePicker(arrival);
                }
                if (!departureField.contains(e.target)) {
                    closePicker(departure);
                }
            });

            // Close on Escape
            document.addEventListener('keydown', (e) => {
                if (e.key === 'Escape') {
                    if (!arrival.popup.hidden) {
                        closePicker(arrival);
                        arrivalInput.focus();
                    }
                    if (!departure.popup.hidden) {
                        closePicker(departure);
                        departureInput.focus();
                    }
                }
            });
        }


        // --- Guests Logic ---
        const guestsComponent = form.querySelector('.js-booking-guests');

        let rooms = [
            { adults: 2, children: 0, infants: 0, childAges: [] }
        ];
        const MAX_ADULTS_CHILDREN = 4;
        const MAX_INFANTS = 2;
        const MIN_CHILD_AGE = 3;
        const MAX_CHILD_AGE = 12;

        if (guestsComponent) {
            const toggleBtn = guestsComponent.querySelector('.js-booking-guests-toggle');
            const panel = guestsComponent.querySelector('.js-booking-guests-panel');
            const roomsContainer = guestsComponent.querySelector('.js-rooms-container');
            const totalGuestsEl = guestsComponent.querySelector('.js-total-guests');
            const totalRoomsEl = guestsComponent.querySelector('.js-total-rooms');

            function updateTotals() {
                let totalGuests = 0;
                rooms.forEach(room => {
                    totalGuests += room.adults + room.children;
                });

                totalGuestsEl.textContent = totalGuests;
                totalRoomsEl.textContent = `ROOMS: ${rooms.length}`;
            }

            function renderRooms() {
                roomsContainer.innerHTML = '';
                rooms.forEach((room, index) => {
                    const roomEl = document.createElement('div');
                    roomEl.className = 'booking-room';

                    const isMultipleRooms = rooms.length > 1;
                    const adultsChildrenTotal = room.adults + room.children;
                    const isAdultsChildrenAtLimit = adultsChildrenTotal >= MAX_ADULTS_CHILDREN;
                    const isInfantsAtLimit = room.infants >= MAX_INFANTS;

                    // One "Child N Age" line per child (default 3, min 3, max 12)
                    const childAgesMarkup = room.childAges.map((age, ageIndex) => `
                        <div class="booking-guest-type booking-guest-type--child-age">
                            <div class="booking-guest-type__label">
                                <span>Child ${ageIndex + 1} Age</span>
                            </div>
                            <div class="booking-guest-type__controls">
                                <button type="button" class="btn-qty js-qty-btn" data-type="childAge" data-action="minus" data-index="${index}" data-child-index="${ageIndex}" aria-label="Decrease age of child ${ageIndex + 1} in room ${index + 1}" ${age <= MIN_CHILD_AGE ? 'disabled' : ''}></button>
                                <span class="qty-value">${age}</span>
                                <button type="button" class="btn-qty js-qty-btn" data-type="childAge" data-action="plus" data-index="${index}" data-child-index="${ageIndex}" aria-label="Increase age of child ${ageIndex + 1} in room ${index + 1}" ${age >= MAX_CHILD_AGE ? 'disabled' : ''}></button>
                            </div>
                        </div>
                    `).join('');

                    roomEl.innerHTML = `
                        <div class="booking-room__header">
                            <h4>Room #${index + 1}</h4>
                        </div>

                        <div class="booking-guest-type">
                            <div class="booking-guest-type__label">
                                <span>Adult</span>
                                <small>13+ years</small>
                            </div>
                            <div class="booking-guest-type__controls">
                                <button type="button" class="btn-qty js-qty-btn" data-type="adults" data-action="minus" data-index="${index}" aria-label="Decrease adults in room ${index + 1}" ${room.adults <= 1 ? 'disabled' : ''}></button>
                                <span class="qty-value">${room.adults}</span>
                                <button type="button" class="btn-qty js-qty-btn" data-type="adults" data-action="plus" data-index="${index}" aria-label="Increase adults in room ${index + 1}" ${isAdultsChildrenAtLimit ? 'disabled' : ''}></button>
                            </div>
                        </div>

                        <div class="booking-guest-type">
                            <div class="booking-guest-type__label">
                                <span>Child</span>
                                <small>3-12 years</small>
                            </div>
                            <div class="booking-guest-type__controls">
                                <button type="button" class="btn-qty js-qty-btn" data-type="children" data-action="minus" data-index="${index}" aria-label="Decrease children in room ${index + 1}" ${room.children <= 0 ? 'disabled' : ''}></button>
                                <span class="qty-value">${room.children}</span>
                                <button type="button" class="btn-qty js-qty-btn" data-type="children" data-action="plus" data-index="${index}" aria-label="Increase children in room ${index + 1}" ${isAdultsChildrenAtLimit ? 'disabled' : ''}></button>
                            </div>
                        </div>

                        <div class="booking-guest-type">
                            <div class="booking-guest-type__label">
                                <span>Infant</span>
                                <small>0-2 years</small>
                            </div>
                            <div class="booking-guest-type__controls">
                                <button type="button" class="btn-qty js-qty-btn" data-type="infants" data-action="minus" data-index="${index}" aria-label="Decrease infants in room ${index + 1}" ${room.infants <= 0 ? 'disabled' : ''}></button>
                                <span class="qty-value">${room.infants}</span>
                                <button type="button" class="btn-qty js-qty-btn" data-type="infants" data-action="plus" data-index="${index}" aria-label="Increase infants in room ${index + 1}" ${isInfantsAtLimit ? 'disabled' : ''}></button>
                            </div>
                        </div>

                        ${childAgesMarkup}

                        <div class="booking-guests-panel__actions">
                            <button type="button" class="btn-add-room js-add-room">+ Add Room</button>
                            ${isMultipleRooms ? `<button type="button" class="btn-remove-room js-remove-room" aria-label="Remove Room ${index + 1}" data-index="${index}"></button>` : ''}
                        </div>
                    `;
                    roomsContainer.appendChild(roomEl);
                });

                const qtyBtns = roomsContainer.querySelectorAll('.js-qty-btn');
                qtyBtns.forEach(btn => {
                    btn.addEventListener('click', (e) => {
                        e.preventDefault();
                        const target = e.currentTarget;
                        const index = parseInt(target.getAttribute('data-index'), 10);
                        const type = target.getAttribute('data-type');
                        const action = target.getAttribute('data-action');

                        const room = rooms[index];
                        if (!room) return;

                        if (type === 'childAge') {
                            const childIndex = parseInt(target.getAttribute('data-child-index'), 10);
                            if (action === 'plus' && room.childAges[childIndex] < MAX_CHILD_AGE) {
                                room.childAges[childIndex]++;
                            } else if (action === 'minus' && room.childAges[childIndex] > MIN_CHILD_AGE) {
                                room.childAges[childIndex]--;
                            }
                        } else if (action === 'plus') {
                            if ((type === 'adults' || type === 'children') && (room.adults + room.children) >= MAX_ADULTS_CHILDREN) {
                                return;
                            }

                            if (type === 'infants' && room.infants >= MAX_INFANTS) {
                                return;
                            }

                            room[type]++;

                            // Adding a child adds an age line (default minimum age)
                            if (type === 'children') {
                                room.childAges.push(MIN_CHILD_AGE);
                            }
                        } else if (action === 'minus') {
                            if (room[type] > 0) {
                                if (type === 'adults' && room[type] <= 1) return;
                                room[type]--;

                                // Removing a child removes its age line
                                if (type === 'children') {
                                    room.childAges.pop();
                                }
                            }
                        }

                        renderRooms();
                        updateTotals();
                    });
                });

                const removeBtns = roomsContainer.querySelectorAll('.js-remove-room');
                removeBtns.forEach(btn => {
                    btn.addEventListener('click', (e) => {
                        e.preventDefault();
                        const index = parseInt(e.currentTarget.getAttribute('data-index'));
                        rooms.splice(index, 1);
                        renderRooms();
                        updateTotals();
                    });
                });

                const addRoomBtns = roomsContainer.querySelectorAll('.js-add-room');
                addRoomBtns.forEach(btn => {
                    btn.addEventListener('click', (e) => {
                        e.preventDefault();
                        rooms.push({ adults: 2, children: 0, infants: 0, childAges: [] });
                        renderRooms();
                        updateTotals();
                    });
                });
            }

            toggleBtn.addEventListener('click', (e) => {
                e.preventDefault();
                const isExpanded = toggleBtn.getAttribute('aria-expanded') === 'true';
                toggleBtn.setAttribute('aria-expanded', !isExpanded);

                if (!isExpanded) {
                    panel.removeAttribute('hidden');
                } else {
                    panel.setAttribute('hidden', '');
                }
            });

            document.addEventListener('click', (e) => {
                if (!e.composedPath().includes(guestsComponent)) {
                    toggleBtn.setAttribute('aria-expanded', 'false');
                    panel.setAttribute('hidden', '');
                }
            });

            renderRooms();
            updateTotals();
        }

        // --- Code Type Field: open native select on box click ---
        const codeTypeField = form.querySelector('.booking-form__field--code-type');
        if (codeTypeField) {
            const codeTypeSelect = codeTypeField.querySelector('select');
            if (codeTypeSelect) {
                codeTypeField.addEventListener('click', (e) => {
                    if (e.target === codeTypeSelect) return;
                    if (typeof codeTypeSelect.showPicker === 'function') {
                        try {
                            codeTypeSelect.showPicker();
                        } catch (err) {
                            codeTypeSelect.focus();
                        }
                    } else {
                        codeTypeSelect.focus();
                    }
                });
            }
        }

        // --- Form Submit Logic ---
        form.addEventListener('submit', (e) => {
            e.preventDefault();

            let cityId = "DEFAULT_CITY";
            let hotelId = "DEFAULT_HOTEL";
            const domain = "greatsouthernkillarney";

            let arrivalDate = "";
            let nights = 1;

            const arrivalCalendar = form.querySelector('.booking-form__field--date:nth-child(1) calendar-date');
            const departureCalendar = form.querySelector('.booking-form__field--date:nth-child(2) calendar-date');

            if (arrivalCalendar && arrivalCalendar.value) {
                arrivalDate = arrivalCalendar.value;

                if (departureCalendar && departureCalendar.value) {
                    const arrivalDateObj = new Date(arrivalDate);
                    const departureDateObj = new Date(departureCalendar.value);
                    const diffTime = Math.abs(departureDateObj - arrivalDateObj);
                    nights = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                    if (nights <= 0) nights = 1;
                }
            }

            const totalRooms = rooms.length;
            let guestsParams = "";

            rooms.forEach((room, index) => {
                const roomIndex = index + 1;
                guestsParams += `&adults[${roomIndex}]=${room.adults}&children[${roomIndex}]=${room.children}`;
                room.childAges.forEach((age, ageIndex) => {
                    guestsParams += `&childAge[${roomIndex}][${ageIndex + 1}]=${age}`;
                });
                guestsParams += `&infants[${roomIndex}]=${room.infants}`;
            });

            const bookingLink = form.querySelector('.booking-form__field--link');
            const codeInput = form.querySelector('.booking-form__field--code input');
            const codeTypeSelect = form.querySelector('.booking-form__field--code-type select');
            const code = codeInput ? encodeURIComponent(codeInput.value.trim()) : "";
            const codeType = codeTypeSelect ? encodeURIComponent(codeTypeSelect.value) : "";
            const bookingUrl = bookingLink && bookingLink.value ? bookingLink.value : `https://bookings.${domain}.ie/rates-room1`;
            const cityCode = form.querySelector(".booking-form__field--city_code");
            const hotelCode = form.querySelector(".booking-form__field--hotel_code");
            if (cityCode) {
                cityId = cityCode.value;
            }
            if (hotelCode) {
                hotelId = hotelCode.value;
            }
            let url = bookingUrl;
            let urlParams = "";
            if (cityId || hotelId || arrivalDate || nights || totalRooms || guestsParams || code) {
                url += "?";
            }
            if (cityId) urlParams = urlParams + `${urlParams ? "&" : ""}city=${cityId}`;
            if (hotelId) urlParams = urlParams + `${urlParams ? "&" : ""}hotel=${hotelId}`;
            if (arrivalDate) urlParams = urlParams + `${urlParams ? "&" : ""}arrival=${arrivalDate}`;
            if (nights) urlParams = urlParams + `${urlParams ? "&" : ""}nights=${nights}`;
            if (totalRooms || guestsParams) urlParams = urlParams + `${urlParams ? "&" : ""}rooms=${totalRooms}${guestsParams}`;
            if (codeType) urlParams = urlParams + `${urlParams ? "&" : ""}codeType=${codeType}`;
            if (code) urlParams = urlParams + `${urlParams ? "&" : ""}code=${code}`;
            url = url + urlParams;
            console.log("Booking Submit URL Generated:");
            console.log(url);
            window.open(url, "_blank");
        });
    });
}

export function initGlobalBooking() {
    const globalBooking = document.querySelector('.js-global-booking');
    if (!globalBooking) return;

    const toggleBtn = globalBooking.querySelector('.js-global-booking-toggle');
    const panel = globalBooking.querySelector('.js-global-booking-panel');

    if (!toggleBtn || !panel) return;

    // Show/hide sticky button based on scroll position
    const handleScroll = () => {
        if (window.scrollY > 200) {
            toggleBtn.classList.add('is-visible');
        } else {
            toggleBtn.classList.remove('is-visible');
            // Auto close if scrolling back up to top
            globalBooking.classList.remove('is-active');
            toggleBtn.setAttribute('aria-expanded', 'false');
        }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    const closeGlobalBooking = () => {
        globalBooking.classList.remove('is-active');
        toggleBtn.setAttribute('aria-expanded', 'false');
        toggleBtn.focus();
    };

    // Toggle button click listener
    toggleBtn.addEventListener('click', (e) => {
        e.preventDefault();
        const isExpanded = toggleBtn.getAttribute('aria-expanded') === 'true';

        if (!isExpanded) {
            toggleBtn.setAttribute('aria-expanded', 'true');
            globalBooking.classList.add('is-active');
        } else {
            closeGlobalBooking();
        }
    });

    // Close when clicking outside of the global booking component entirely or on the overlay
    document.addEventListener('click', (e) => {
        const isOutside = !e.composedPath().includes(globalBooking);
        const isOverlay = e.target.classList.contains('js-global-booking-overlay');

        if ((isOutside || isOverlay) && globalBooking.classList.contains('is-active')) {
            closeGlobalBooking();
        }
    });

    // Keyboard accessibility: ESC to close, focus trap
    document.addEventListener('keydown', (e) => {
        if (!globalBooking.classList.contains('is-active')) return;

        if (e.key === 'Escape') {
            closeGlobalBooking();
            return;
        }

        if (e.key === 'Tab') {
            const focusableElements = globalBooking.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
            const elements = Array.from(focusableElements).filter(el => {
                return !el.disabled && el.clientWidth > 0 && el.clientHeight > 0;
            });

            if (elements.length === 0) return;

            const firstElement = elements[0];
            const lastElement = elements[elements.length - 1];

            if (e.shiftKey) { // Shift + Tab
                if (document.activeElement === firstElement || document.activeElement === document.body) {
                    e.preventDefault();
                    lastElement.focus();
                }
            } else { // Tab
                if (document.activeElement === lastElement || !globalBooking.contains(document.activeElement)) {
                    e.preventDefault();
                    firstElement.focus();
                }
            }
        }
    });
}
